import React, { createContext, useContext, useEffect, useState } from 'react';

const linkedinOAuthOpenIDScopes = [
  'openid',
  'email',
  'profile'
]

interface User {
  sub: string;
  email_verified: boolean;
  name: string;
  locale: {
    country: string;
    language: string;
  };
  given_name: string;
  family_name: string;
  email: string;
  picture: string;
  token: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
}

export type AuthContextType = {
  authState: AuthState;
  handleLinkedInAuth: () => void;
  logOut: () => void;
  setAuthState: (authState: { isAuthenticated: boolean; user: User | null }) => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getInitialAuthState = (): AuthState => {
  const storedState = localStorage.getItem('authState');
  return storedState
    ? JSON.parse(storedState)
    : { isAuthenticated: false, user: null };
};

export const AuthProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [authState, setAuthState] = useState<AuthState>(getInitialAuthState);

  useEffect(() => {
    localStorage.setItem('authState', JSON.stringify(authState));
  }, [authState]);

  const handleLinkedInAuth = () => {
    const linkedinAuthUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${process.env.REACT_APP_LINKEDIN_CLIENT_ID}&redirect_uri=${process.env.REACT_APP_LINKEDIN_REDIRECT_URI}&scope=${linkedinOAuthOpenIDScopes.join(',')}`;
    window.location.href = linkedinAuthUrl;
  };

  const logOut = () => {
    setAuthState({
      isAuthenticated: false,
      user: null,
    });
    localStorage.removeItem('authState');
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ authState, handleLinkedInAuth, logOut, setAuthState }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}; 